import type { FormSelectOption } from '@practium/ui';
import { MONTH_NAMES_PT } from '../constants/maintenance.constants';

export const CYCLE_START_PHASES = ['preFlowering', 'postHarvest'] as const;
export const FLOWERING_PHASES = ['flowering'] as const;

export type MaintenanceParcellingContext = {
	floweringMonths: number[];
	cycleStartMonths: number[];
};

type ParcelMonth = {
	month?: number;
	phase?: string;
};

export const parseMonthNumber = (value?: string | number | null): number | null => {
	if (value == null || value === '') return null;
	if (typeof value === 'number') {
		return Number.isInteger(value) && value >= 1 && value <= 12 ? value : null;
	}
	const normalized = String(value).replace(/\//g, '-');
	if (/^\d{4}-\d{2}/.test(normalized)) {
		const month = Number(normalized.slice(5, 7));
		return month >= 1 && month <= 12 ? month : null;
	}
	const month = Number(normalized);
	return Number.isInteger(month) && month >= 1 && month <= 12 ? month : null;
};

export const getParcellingMonthsByPhase = (
	parcels: ParcelMonth[] | undefined,
	phases: readonly string[]
) => {
	const months = new Set<number>();
	for (const parcel of parcels ?? []) {
		if (!phases.includes(parcel.phase ?? '')) continue;
		const month = parseMonthNumber(parcel.month);
		if (month != null) months.add(month);
	}
	return [...months].sort((a, b) => a - b);
};

export const toMonthSelectOptions = (months: number[]): FormSelectOption[] =>
	months.map((month) => {
		const key = String(month).padStart(2, '0');
		return { value: String(month), label: MONTH_NAMES_PT[key] ?? key };
	});

export const monthNumberLabel = (value?: string | number | null) => {
	const month = parseMonthNumber(value);
	if (month == null) return '';
	return MONTH_NAMES_PT[String(month).padStart(2, '0')] ?? String(month);
};

export const findCycleStartIndex = (parcels: ParcelMonth[] | undefined, startMonth?: string | number | null) => {
	const month = parseMonthNumber(startMonth);
	if (month == null) return -1;
	return (parcels ?? []).findIndex(
		(parcel) =>
			Number(parcel.month) === month &&
			CYCLE_START_PHASES.includes(parcel.phase as (typeof CYCLE_START_PHASES)[number])
	);
};

export const validateFixedParcellingMonths = (
	floweringMonth: string,
	cycleStartMonth: string,
	floweringMonths: number[],
	cycleStartMonths: number[]
): Record<string, string> => {
	const floweringNumber = parseMonthNumber(floweringMonth);
	if (floweringMonths.length > 0 && (floweringNumber == null || !floweringMonths.includes(floweringNumber))) {
		return { floweringMonth: 'O parcelamento selecionado não possui florada neste mês.' };
	}

	const cycleStart = Number(cycleStartMonth);
	if (cycleStartMonths.length > 0 && !cycleStartMonths.includes(cycleStart)) {
		return {
			cycleStartMonth: 'O início do ciclo deve coincidir com um mês de pré-florada ou recuperação.',
		};
	}

	return {};
};
